import { z } from 'zod'

/** Android `SCREEN_BRIGHTNESS_MODE`: automatic (ambient-light) vs manual. */
export const PLAYER_BRIGHTNESS_MODES = ['auto', 'manual'] as const
export const playerBrightnessModeSchema = z.enum(PLAYER_BRIGHTNESS_MODES)
export type TPlayerBrightnessMode = z.infer<typeof playerBrightnessModeSchema>

/**
 * Whether the operator can steer this screen's backlight. HDMI TV boxes / sticks drive an external
 * monitor whose brightness Android cannot reach, so they report `unsupported`.
 */
export const PLAYER_BRIGHTNESS_CONTROLS = ['supported', 'unsupported'] as const
export const playerBrightnessControlSchema = z.enum(PLAYER_BRIGHTNESS_CONTROLS)
export type TPlayerBrightnessControl = z.infer<typeof playerBrightnessControlSchema>

/**
 * How the player applies brightness: `window` = per-window override (no permission, only while the
 * player is in the foreground — the kiosk case), `system` = global `Settings.System` (needs the
 * `WRITE_SETTINGS` app-op).
 */
export const PLAYER_BRIGHTNESS_METHODS = ['window', 'system'] as const
export const playerBrightnessMethodSchema = z.enum(PLAYER_BRIGHTNESS_METHODS)
export type TPlayerBrightnessMethod = z.infer<typeof playerBrightnessMethodSchema>

/** A screen forced to ~0% looks dead and is hard to recover remotely, so operators cannot go below this. */
export const PLAYER_BRIGHTNESS_MIN_PERCENT = 5 as const
export const PLAYER_BRIGHTNESS_MAX_PERCENT = 100 as const

/** Operator brightness route budget, per (user, player), per 60s window. The UI commits on slider release. */
export const PLAYER_SCREEN_BRIGHTNESS_PER_PLAYER_MAX_PER_MINUTE = 30 as const

/** A reported percent move smaller than this is persisted but not fanned out to operators. */
export const PLAYER_BRIGHTNESS_PERCENT_EMIT_DELTA = 5 as const

export const screenBrightnessPercentSchema = z
  .number()
  .int()
  .min(PLAYER_BRIGHTNESS_MIN_PERCENT)
  .max(PLAYER_BRIGHTNESS_MAX_PERCENT)

const hhmmSchema = z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, 'expected HH:MM (24h)')

export const PLAYER_BRIGHTNESS_SCHEDULE_MAX_SLOTS = 8 as const

/** A daily window `[start, end)` in the player's local time; `start > end` wraps past midnight (e.g. 19:00–07:00). */
export const brightnessScheduleSlotSchema = z
  .object({ start: hhmmSchema, end: hhmmSchema, percent: screenBrightnessPercentSchema })
  .strict()
  .refine(slot => slot.start !== slot.end, { message: 'start and end must differ' })
export type TBrightnessScheduleSlot = z.infer<typeof brightnessScheduleSlotSchema>

/** Ambient-light curve: lux is mapped log-scale from `minLux..maxLux` onto `minPercent..maxPercent`. */
export const PLAYER_BRIGHTNESS_SENSOR_MAX_LUX = 100_000 as const
export const brightnessSensorConfigSchema = z
  .object({
    mode: z.literal('sensor'),
    minPercent: screenBrightnessPercentSchema,
    maxPercent: screenBrightnessPercentSchema,
    minLux: z.number().min(0).max(PLAYER_BRIGHTNESS_SENSOR_MAX_LUX),
    maxLux: z.number().min(1).max(PLAYER_BRIGHTNESS_SENSOR_MAX_LUX)
  })
  .strict()
  .refine(c => c.minPercent <= c.maxPercent, { message: 'minPercent must not exceed maxPercent' })
  .refine(c => c.minLux < c.maxLux, { message: 'minLux must be below maxLux' })
export type TBrightnessSensorConfig = z.infer<typeof brightnessSensorConfigSchema>

export const brightnessScheduleConfigSchema = z
  .object({
    mode: z.literal('schedule'),
    slots: z.array(brightnessScheduleSlotSchema).min(1).max(PLAYER_BRIGHTNESS_SCHEDULE_MAX_SLOTS)
  })
  .strict()
export type TBrightnessScheduleConfig = z.infer<typeof brightnessScheduleConfigSchema>

/**
 * Hands the brightness back to Android's own adaptive brightness (`SCREEN_BRIGHTNESS_MODE_AUTOMATIC`).
 * The player can only do that as Device Owner or with `WRITE_SETTINGS`; otherwise (or on an APK without
 * the native support) it keeps the fixed fallback `percent`.
 */
export const brightnessSystemAutoConfigSchema = z.object({ mode: z.literal('system') }).strict()
export type TBrightnessSystemAutoConfig = z.infer<typeof brightnessSystemAutoConfigSchema>

/**
 * Automatic brightness on top of the fixed target (`percent`). Absent/null = plain fixed brightness.
 * `percent` stays the fallback: outside every schedule slot, while the sensor has no reading, and when
 * Android's adaptive brightness cannot be enabled.
 */
export const brightnessAutoConfigSchema = z.union([
  brightnessScheduleConfigSchema,
  brightnessSensorConfigSchema,
  brightnessSystemAutoConfigSchema
])
export type TBrightnessAutoConfig = z.infer<typeof brightnessAutoConfigSchema>

export const DEFAULT_BRIGHTNESS_SENSOR_CONFIG: TBrightnessSensorConfig = {
  mode: 'sensor',
  minPercent: 20,
  maxPercent: 100,
  minLux: 10,
  maxLux: 1000
}

/**
 * Operator request. `percent: null` releases all control. `auto: null` = plain fixed brightness;
 * omitted = leave the current automatic config untouched. An automatic mode needs a fallback `percent`.
 */
export const setScreenBrightnessBodySchema = z
  .object({
    percent: screenBrightnessPercentSchema.nullable(),
    auto: brightnessAutoConfigSchema.nullable().optional()
  })
  .strict()
  .refine(body => body.auto == null || body.percent != null, {
    message: 'an automatic brightness mode needs a fallback percent',
    path: ['percent']
  })
export type TSetScreenBrightnessBody = z.infer<typeof setScreenBrightnessBodySchema>

/** WebSocket payload for `set-screen-brightness` (API → player). Older players ignore `auto`. */
export const setScreenBrightnessWsPayloadSchema = z.object({
  percent: screenBrightnessPercentSchema.nullable(),
  auto: brightnessAutoConfigSchema.nullable().optional()
})
export type TSetScreenBrightnessWsPayload = z.infer<typeof setScreenBrightnessWsPayloadSchema>

/** Zod fragments shared by the heartbeat body. */
export const playerBrightnessHeartbeatFieldsSchema = {
  brightnessPercent: z.number().int().min(0).max(100).optional(),
  brightnessMode: playerBrightnessModeSchema.optional(),
  brightnessControl: playerBrightnessControlSchema.optional(),
  brightnessMethod: playerBrightnessMethodSchema.optional(),
  writeSettingsGranted: z.boolean().optional(),
  /** Device has an ambient light sensor (`TYPE_LIGHT`). */
  ambientLightSupported: z.boolean().optional(),
  /** Latest ambient light reading, only while the sensor is being sampled. */
  ambientLux: z.number().int().min(0).max(PLAYER_BRIGHTNESS_SENSOR_MAX_LUX * 2).optional()
}

export interface IPlayerBrightnessStatus {
  brightnessPercent?: number | null
  brightnessMode?: TPlayerBrightnessMode | null
  brightnessControl?: TPlayerBrightnessControl | null
  brightnessMethod?: TPlayerBrightnessMethod | null
  writeSettingsGranted?: boolean | null
  ambientLightSupported?: boolean | null
  ambientLux?: number | null
}

/**
 * Whether a new sample should be broadcast to operators: control / method / mode / permission flips
 * always, percent only when it moved by at least {@link PLAYER_BRIGHTNESS_PERCENT_EMIT_DELTA}.
 */
export function shouldEmitBrightnessChange(prev: IPlayerBrightnessStatus, next: IPlayerBrightnessStatus): boolean {
  if (next.brightnessControl !== undefined && next.brightnessControl !== prev.brightnessControl) {
    return true
  }
  if (next.brightnessMethod !== undefined && next.brightnessMethod !== prev.brightnessMethod) {
    return true
  }
  if (next.brightnessMode !== undefined && next.brightnessMode !== prev.brightnessMode) {
    return true
  }
  if (next.writeSettingsGranted !== undefined && next.writeSettingsGranted !== prev.writeSettingsGranted) {
    return true
  }
  if (next.ambientLightSupported !== undefined && next.ambientLightSupported !== prev.ambientLightSupported) {
    return true
  }
  if (typeof next.brightnessPercent !== 'number') {
    return false
  }
  if (typeof prev.brightnessPercent !== 'number') {
    return true
  }
  return Math.abs(next.brightnessPercent - prev.brightnessPercent) >= PLAYER_BRIGHTNESS_PERCENT_EMIT_DELTA
}

/** The control is shown to operators unless the device explicitly reported it cannot be steered. */
export function isBrightnessControllable(status: IPlayerBrightnessStatus): boolean {
  return status.brightnessControl !== 'unsupported'
}

/**
 * Lux flutters constantly, so a reading is only persisted when it moved meaningfully
 * (>= 25 % or >= 10 lux), and it never triggers an operator fan-out on its own.
 */
export function shouldPersistAmbientLux(prev: number | null | undefined, next: number): boolean {
  if (typeof prev !== 'number') {
    return true
  }
  return Math.abs(next - prev) >= Math.max(10, prev * 0.25)
}

// ---------------------------------------------------------------------------------------------
// Automatic brightness (pure; used by the player and unit-tested here)
// ---------------------------------------------------------------------------------------------

export function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':')
  return Number(h) * 60 + Number(m)
}

/** Local minutes since midnight for `date` (the player's own clock/timezone). */
export function localMinutesOfDay(date: Date): number {
  return date.getHours() * 60 + date.getMinutes()
}

/**
 * Brightness for `nowMinutes` from the schedule: the first slot (in list order) containing the
 * moment wins, otherwise `fallbackPercent`. Slots are `[start, end)`; `start > end` wraps midnight.
 */
export function resolveScheduledBrightnessPercent(
  slots: readonly TBrightnessScheduleSlot[],
  fallbackPercent: number,
  nowMinutes: number
): number {
  for (const slot of slots) {
    const start = hhmmToMinutes(slot.start)
    const end = hhmmToMinutes(slot.end)
    const inside = start < end
      ? nowMinutes >= start && nowMinutes < end
      : nowMinutes >= start || nowMinutes < end
    if (inside) {
      return slot.percent
    }
  }
  return fallbackPercent
}

/** Log-scale lux → brightness %: dim rooms move the needle more than a bright room getting brighter. */
export function mapLuxToBrightnessPercent(lux: number, config: TBrightnessSensorConfig): number {
  const { minPercent, maxPercent, maxLux } = config
  const minLux = Math.max(config.minLux, 1)
  if (!Number.isFinite(lux) || lux <= minLux) {
    return minPercent
  }
  if (lux >= maxLux) {
    return maxPercent
  }
  const t = (Math.log(lux) - Math.log(minLux)) / (Math.log(maxLux) - Math.log(minLux))
  return Math.round(minPercent + t * (maxPercent - minPercent))
}

export interface IBrightnessSmootherOptions {
  /** EMA weight of the newest sample in log-lux space (0–1). */
  alpha?: number
  /** Ignore target moves smaller than this many percent points, so the screen doesn't hunt. */
  minDeltaPercent?: number
}

/**
 * Turns noisy lux samples into occasional brightness changes: EMA in log space, then hysteresis.
 * `feed` returns the percent to apply, or null when the change is too small to bother the screen.
 */
export function createBrightnessSmoother(options: IBrightnessSmootherOptions = {}) {
  const alpha = options.alpha ?? 0.3
  const minDelta = options.minDeltaPercent ?? 3
  let ema: number | null = null
  let applied: number | null = null
  return {
    feed(lux: number, config: TBrightnessSensorConfig): number | null {
      const x = Math.log(Math.max(lux, 0) + 1)
      ema = ema == null ? x : ema + alpha * (x - ema)
      const target = mapLuxToBrightnessPercent(Math.exp(ema) - 1, config)
      const atExtreme = target === config.minPercent || target === config.maxPercent
      if (applied == null || Math.abs(target - applied) >= minDelta || (atExtreme && target !== applied)) {
        applied = target
        return target
      }
      return null
    },
    reset() {
      ema = null
      applied = null
    }
  }
}
